import { Icon } from '@iconify/react';
import { Badge } from '@/components/shadcn/badge';
import { Card } from '@/components/shadcn/card';
import type { MabarEvent } from '@/services/communityService';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';

const formatEventDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

function eventStatusLabel(event: MabarEvent): string {
  const now = new Date();
  const startTime = event.start_time ? new Date(event.start_time) : null;
  const endTime = event.end_time ? new Date(event.end_time) : null;
  if (endTime && now >= endTime) return 'SELESAI';
  if (startTime && now >= startTime && (!endTime || now < endTime)) return 'BERLANGSUNG';
  return 'MENDATANG';
}

interface BadmintonEventHeaderProps {
  event: MabarEvent;
}

export default function BadmintonEventHeader({ event }: BadmintonEventHeaderProps) {
  const venue = event.lokasi_arena || event.address;
  const timeRange = formatLocalEventTimeRange(event.start_time, event.end_time);
  const status = eventStatusLabel(event);
  const sportName = event.sport_category?.name || 'Badminton';

  const row = (icon: string, label: string, value: string) => (
    <div className="flex items-start gap-3">
      <Icon icon={icon} className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FC480D]" />
      <div className="min-w-0">
        <p className="text-[11px] text-gray-400">{label}</p>
        <p className="text-sm font-extrabold text-[#0E1726]">{value}</p>
      </div>
    </div>
  );

  return (
    <Card className="rounded-2xl border-0 bg-white p-4 shadow-[inset_0_0_0_1px_#E0E6ED]">
      <div className="flex items-center justify-between gap-2">
        <Badge className="border-0 bg-[#FFF1EC] px-2.5 py-1 text-[11px] font-bold text-[#FC480D] hover:bg-[#FFF1EC]">
          {sportName}
        </Badge>
        <Badge
          className={`border-0 px-2.5 py-1 text-[11px] font-bold hover:bg-inherit ${
            status === 'SELESAI'
              ? 'bg-emerald-50 text-emerald-600'
              : status === 'BERLANGSUNG'
                ? 'bg-amber-50 text-amber-600'
                : 'bg-blue-50 text-blue-600'
          }`}
        >
          {status}
        </Badge>
      </div>
      <h2 className="mt-3 text-lg font-extrabold text-[#0E1726]">{event.name}</h2>
      <div className="mt-4 space-y-3">
        {row(
          'mdi:calendar-blank-outline',
          'Tanggal & Waktu',
          `${formatEventDate(event.event_date)}${timeRange ? ` · ${timeRange}` : ''}`
        )}
        {event.host?.name && row('mdi:account-outline', 'Host / Komunitas', event.host.name)}
        {venue && row('mdi:map-marker-outline', 'Venue', venue)}
        {row('mdi:badminton', 'Jenis Olahraga', sportName)}
      </div>
    </Card>
  );
}
